import type { UseQueryResult } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';

/**
 * Variante multi-valor do `AsyncSelect`: mesmo shell (Popover + Command),
 * mas mantém o popover aberto ao selecionar e guarda um array de valores.
 * Aceita tanto uma lista estática (`options`) quanto uma fonte via API
 * (`useQuery`) — só um dos dois deve ser passado.
 */
export interface MultiSelectProps<T> {
  useQuery?: (search?: string) => UseQueryResult<T[], Error>;
  options?: T[];
  filterFn?: (option: T, query: string) => boolean;
  renderOption?: (option: T) => React.ReactNode;
  getOptionValue: (option: T) => any;
  getDisplayValue: (option: T) => React.ReactNode;
  notFound?: React.ReactNode;
  value: any[];
  onChange: (values: any[], options: T[]) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  triggerClassName?: string;
  noResultsMessage?: string;
  align?: 'start' | 'center' | 'end';
}

export function MultiSelect<T>({
  useQuery,
  options: staticOptions,
  filterFn,
  renderOption,
  getOptionValue,
  getDisplayValue,
  notFound,
  label,
  placeholder = 'Selecione...',
  value = [],
  onChange,
  disabled = false,
  fullWidth = true,
  className,
  triggerClassName,
  noResultsMessage,
  align = 'start',
}: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const shouldFetch = !!useQuery && open;
  const queryResult = useQuery?.(shouldFetch ? debouncedSearchTerm : undefined);
  const isLoading = useQuery ? (queryResult?.isLoading ?? false) : false;
  const isError = useQuery ? (queryResult?.isError ?? false) : false;
  const error = queryResult?.error;

  const fetchedData = useMemo(() => {
    if (useQuery) return queryResult?.data ?? [];

    const list = staticOptions ?? [];
    if (!debouncedSearchTerm) return list;

    return list.filter((option) =>
      filterFn
        ? filterFn(option, debouncedSearchTerm)
        : String(getDisplayValue(option))
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()),
    );
  }, [
    useQuery,
    queryResult?.data,
    staticOptions,
    debouncedSearchTerm,
    filterFn,
    getDisplayValue,
  ]);

  const allOptions = useMemo(
    () => (useQuery ? (queryResult?.data ?? []) : (staticOptions ?? [])),
    [useQuery, queryResult?.data, staticOptions],
  );

  const selectedOptions = useMemo(
    () =>
      value
        .map((v) => allOptions.find((option) => getOptionValue(option) == v))
        .filter((o): o is T => !!o),
    [value, allOptions, getOptionValue],
  );

  const handleToggle = useCallback(
    (option: T) => {
      const optionValue = getOptionValue(option);
      const exists = value.some((v) => v == optionValue);

      if (exists) {
        const nextValues = value.filter((v) => v != optionValue);
        onChange(
          nextValues,
          nextValues
            .map((v) => allOptions.find((o) => getOptionValue(o) == v))
            .filter((o): o is T => !!o),
        );
      } else {
        const nextValues = [...value, optionValue];
        onChange(nextValues, [...selectedOptions, option]);
      }
    },
    [value, onChange, getOptionValue, allOptions, selectedOptions],
  );

  const handleRemove = useCallback(
    (optionValue: any) => {
      const nextValues = value.filter((v) => v != optionValue);
      onChange(
        nextValues,
        nextValues
          .map((v) => allOptions.find((o) => getOptionValue(o) == v))
          .filter((o): o is T => !!o),
      );
    },
    [value, onChange, allOptions, getOptionValue],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          requestAnimationFrame(() => {
            if (triggerRef.current) setPopoverWidth(triggerRef.current.offsetWidth);
            inputRef.current?.focus();
          });
        } else {
          setSearchTerm('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-auto min-h-9 justify-between',
            disabled && 'opacity-50 cursor-not-allowed',
            fullWidth && 'w-full',
            triggerClassName,
          )}
          disabled={disabled}
        >
          {selectedOptions.length ? (
            <div className="flex flex-wrap gap-1 py-0.5">
              {selectedOptions.map((option) => (
                <Badge
                  key={getOptionValue(option)}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {getDisplayValue(option)}
                  <span
                    role="button"
                    tabIndex={-1}
                    className="cursor-pointer opacity-70 hover:opacity-100"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemove(getOptionValue(option));
                    }}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))}
            </div>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', className)}
        style={{ width: popoverWidth }}
        align={align}
      >
        <Command className="w-full">
          <div className="relative flex items-center border-b px-3 w-full">
            <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <Input
              ref={inputRef}
              placeholder={`Pesquisar ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 border-0 bg-transparent pl-0 focus-visible:ring-0 focus-visible:ring-offset-0 flex-1"
              disabled={isLoading}
            />
            {isLoading && (
              <div className="ml-2 flex items-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </div>
          <CommandList>
            {isError && (
              <div className="p-4 text-destructive text-center">
                {error?.message || 'Erro ao buscar opções'}
              </div>
            )}
            {!isLoading &&
              !isError &&
              fetchedData.length === 0 &&
              (notFound || (
                <CommandEmpty>
                  {noResultsMessage ?? 'Nenhum resultado encontrado.'}
                </CommandEmpty>
              ))}
            <CommandGroup className="w-full">
              {fetchedData.map((option) => {
                const optionValue = getOptionValue(option);
                const checked = value.some((v) => v == optionValue);

                return (
                  <CommandItem
                    key={optionValue}
                    value={optionValue}
                    onSelect={() => handleToggle(option)}
                    className="flex w-full items-center justify-between"
                  >
                    <div className="flex-1">
                      {renderOption ? renderOption(option) : getDisplayValue(option)}
                    </div>
                    <Check
                      className={cn(
                        'ml-2 h-4 w-4 shrink-0',
                        checked ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
